import React, { useState } from 'react'
import { Heart, Target, Users, Calendar, ChevronLeft, ChevronRight, Share2 } from 'lucide-react'
import './RaiseFund.css'

const makePlaceholder = (color, label) => `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect width="600" height="400" fill="${color}"/><text x="300" y="185" text-anchor="middle" font-size="18" fill="rgba(255,255,255,0.9)" font-family="sans-serif" font-weight="bold">${label}</text><text x="300" y="215" text-anchor="middle" font-size="12" fill="rgba(255,255,255,0.6)" font-family="sans-serif">Replace with actual photo</text></svg>`)}`

const campaigns = [
  {
    id: 'hostel-2024',
    title: 'New Hostel Block for 100 Children at Panasput, Chitrakonda, Malkangiri district of Odisha, India',
    desc: 'Our current hostel serves 120 children at Balimela and different rural location, but we receive requests from 80 more each year who must be turned away due to lack of space. This fund will build a new two-storey block with dormitories, a study hall, and sanitation facilities.',
    target: 16000000,
    raised: 106000,
    donors: 4,
    deadline: 'March 2027',
    images: [
      { src: makePlaceholder('#0369a1', 'Hostel Construction Site'), caption: 'Foundation work in progress — July 2025' },
      { src: makePlaceholder('#0284c7', 'Hostel Plan'), caption: 'Architect\'s plan for the new two-storey block' },
      { src: makePlaceholder('#0ea5e9', 'Children Waiting'), caption: 'Children from distant villages waiting for hostel space' },
    ],
    urgent: true,
  },
  {
    id: 'computer-lab',
    title: 'New Computer Lab — 30 New Systems',
    desc: 'Computer education bridges the digital divide, empowering underprivileged children with essential technical skills to break the cycle of poverty and compete equally in the modern job market.',
    target: 20000000,
    raised: 410000,
    donors: 16,
    deadline: 'December 2026',
    images: [
      { src: makePlaceholder('#6d28d9', 'No Lab'), caption: 'Unlocks Limitless Knowledge && Levels the Playing Field: It ensures underprivileged children have access to the same digital skills and tools as their more privileged peers.' },
      { src: makePlaceholder('#7c3aed', 'No Computers'), caption: 'Develops Critical Thinking, Enhances Employability, Enables Self-Directed Learning, Reduces the Inequality Gap' },
    ],
    urgent: false,
  },
  {
    id: 'mid-day-meal',
    title: 'Mid-Day Meal Program for 300 Children',
    desc: 'Many children from outlying villages attend our day school and go without a proper meal. A sustained mid-day meal program will improve concentration, attendance, and the overall health of 300 day-scholar students for one full academic year.',
    target: 45000000,
    raised: 180000,
    donors: 98,
    deadline: 'Ongoing',
    images: [
      { src: makePlaceholder('#b45309', 'Meal Distribution'), caption: 'Volunteers distributing meals during a pilot program' },
      { src: makePlaceholder('#d97706', 'Kitchen Setup'), caption: 'Community kitchen to be set up at the school premises' },
    ],
    urgent: false,
  },
]

function ProgressBar({ raised, target }) {
  const pct = Math.min(100, Math.round((raised / target) * 100))
  return (
    <div className="progress-wrap">
      <div className="progress-bar">
        <div className="progress-bar__fill" style={{ width: `${pct}%` }}>
          <div className="progress-bar__shine"/>
        </div>
      </div>
      <div className="progress-labels">
        <span className="progress-raised">₹{raised.toLocaleString()} raised</span>
        <span className="progress-pct">{pct}%</span>
      </div>
    </div>
  )
}

function ImageGallery({ images }) {
  const [cur, setCur] = useState(0)
  const go = d => setCur(c => (c + d + images.length) % images.length)
  return (
    <div className="campaign-gallery">
      <div className="campaign-gallery__main">
        <img src={images[cur].src} alt={images[cur].caption} className="campaign-gallery__img" loading="lazy"/>
        {images.length > 1 && (
          <>
            <button className="campaign-gallery__nav campaign-gallery__nav--prev" onClick={() => go(-1)}><ChevronLeft size={18}/></button>
            <button className="campaign-gallery__nav campaign-gallery__nav--next" onClick={() => go(1)}><ChevronRight size={18}/></button>
          </>
        )}
        <div className="campaign-gallery__caption">{images[cur].caption}</div>
      </div>
      {images.length > 1 && (
        <div className="campaign-gallery__thumbs">
          {images.map((img, i) => (
            <button key={i} className={`campaign-gallery__thumb${i === cur ? ' active' : ''}`} onClick={() => setCur(i)}>
              <img src={img.src} alt={img.caption}/>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function DonateModal({ campaign, onClose }) {
  const [amount, setAmount] = useState(500)
  const [custom, setCustom] = useState('')
  const [done, setDone] = useState(false)
  const amounts = [500, 1000, 2500, 5000]
  const final = custom ? parseInt(custom) || 0 : amount

  const submit = () => {
    if (final < 1) return
    setDone(true)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box card" onClick={e => e.stopPropagation()}>
        {done ? (
          <div style={{textAlign:'center', padding:'16px'}}>
            <Heart size={44} fill="#0ea5e9" color="#0ea5e9" style={{margin:'0 auto 16px'}}/>
            <h3>Thank You!</h3>
            <p style={{color:'var(--gray-600)', margin:'12px 0 20px'}}>Your donation of <strong>₹{final.toLocaleString()}</strong> towards "<em>{campaign.title}</em>" has been received.</p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <h3 className="modal-box__title">{campaign.title}</h3>
            <p className="modal-box__sub">Select an amount to donate:</p>
            <div className="modal-amounts">
              {amounts.map(a => (
                <button key={a} className={`amount-btn${amount === a && !custom ? ' active' : ''}`} onClick={() => { setAmount(a); setCustom('') }}>₹{a.toLocaleString()}</button>
              ))}
            </div>
            <input type="number" className="form-input" placeholder="Custom amount (₹)" value={custom} onChange={e => { setCustom(e.target.value); setAmount(0) }} min="1" style={{marginBottom:'16px'}}/>
            <button className="btn btn-primary" style={{width:'100%', justifyContent:'center'}} onClick={submit} disabled={final < 1}>
              <Heart size={16} fill="white"/> Donate ₹{final > 0 ? final.toLocaleString() : '—'}
            </button>
            <button className="modal-close" onClick={onClose}>Cancel</button>
          </>
        )}
      </div>
    </div>
  )
}

export default function RaiseFund() {
  const [activeModal, setActiveModal] = useState(null)
  const activeCampaign = campaigns.find(c => c.id === activeModal)

  return (
    <div className="raisefund-page">
      {activeModal && activeCampaign && (
        <DonateModal campaign={activeCampaign} onClose={() => setActiveModal(null)}/>
      )}

      <section className="raisefund-hero">
        <div className="container raisefund-hero__inner">
          <span className="section-tag">Crowdfunding</span>
          <h1 className="section-title" style={{color:'var(--sky-900)'}}>Active Fundraising Campaigns</h1>
          <p className="section-desc" style={{margin:'0 auto'}}>
            Each campaign addresses a specific, urgent need. Track the progress, see the impact, and donate directly to the cause that moves you.
          </p>
        </div>
      </section>

      <section className="page-section">
        <div className="container">
          <div className="campaigns-list">
            {campaigns.map((campaign, idx) => (
              <div key={campaign.id} className={`campaign-card card ${campaign.urgent ? 'campaign-card--urgent' : ''}`}>
                {campaign.urgent && <div className="campaign-badge">🔥 Urgent</div>}
                <div className={`campaign-inner ${idx % 2 === 1 ? 'campaign-inner--rev' : ''}`}>
                  <div className="campaign-gallery-wrap">
                    <ImageGallery images={campaign.images}/>
                  </div>
                  <div className="campaign-content">
                    <h2 className="campaign-title">{campaign.title}</h2>
                    <p className="campaign-desc">{campaign.desc}</p>

                    <ProgressBar raised={campaign.raised} target={campaign.target}/>

                    <div className="campaign-meta">
                      <div className="campaign-meta-item">
                        <Target size={15}/>
                        <span>Goal: <strong>₹{campaign.target.toLocaleString()}</strong></span>
                      </div>
                      <div className="campaign-meta-item">
                        <Users size={15}/>
                        <span><strong>{campaign.donors}</strong> donors</span>
                      </div>
                      <div className="campaign-meta-item">
                        <Calendar size={15}/>
                        <span>Deadline: <strong>{campaign.deadline}</strong></span>
                      </div>
                    </div>

                    <div className="campaign-actions">
                      <button className="btn btn-primary" onClick={() => setActiveModal(campaign.id)}>
                        <Heart size={16} fill="white"/> Donate to This Campaign
                      </button>
                      <button className="btn btn-outline campaign-share" onClick={() => { try { navigator.share({ title: campaign.title, url: window.location.href }) } catch {} }}>
                        <Share2 size={15}/> Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
